import { Form, Heading, Question, QuestionProps, QuestionType, Section } from '@module/form';
import type {
  QuizData,
  QuizSection,
  QuizHeading,
  QuizQuestion,
  QuizAnswer,
  QuizCorrectAnswer,
  QuizSubQuestion,
} from './types';
import { NoneValue } from '@module/form';

interface FormJsonToMarkdownConverterInput {
  form: Form;
  questions: Question<QuestionProps>[];
  sections?: Section[];
  headings?: Heading[];
  /** IDs of mentioned resources (question, heading, section) to mark with @Primary in output */
  primaryIds?: string[];
}

export class FormJsonToMarkdownConverter {
  public static execute(input: FormJsonToMarkdownConverterInput): string {
    const quizData = FormJsonToMarkdownConverter.domainToQuizData(
      input.form,
      input.questions,
      input.sections,
      input.headings,
    );

    const primarySet =
      input.primaryIds && input.primaryIds.length > 0 ? new Set(input.primaryIds) : undefined;
    return FormJsonToMarkdownConverter.quizDataToMarkdown(quizData, primarySet);
  }

  private static domainToQuizData(
    form: Form,
    questions: Question<QuestionProps>[],
    sections?: Section[],
    headings?: Heading[],
  ): QuizData {
    let sectionsToUse = sections && sections.length > 0 ? sections : (form.sections ?? []);
    if (questions.length > 0) {
      const sectionIdsFromQuestions = new Set(questions.map((q) => q.sectionId));
      const existingSectionIds = new Set(sectionsToUse.map((s) => s.id.toString()));
      const formSections = form.sections ?? [];
      const sectionsToAdd = formSections.filter(
        (s) =>
          sectionIdsFromQuestions.has(s.id.toString()) && !existingSectionIds.has(s.id.toString()),
      );
      if (sectionsToAdd.length > 0) {
        sectionsToUse = [...sectionsToUse, ...sectionsToAdd];
      }
    }

    const headingsToUse = headings && headings.length > 0 ? headings : (form.headings ?? []);
    const convertedSections = FormJsonToMarkdownConverter.mapSections(
      form,
      questions,
      sectionsToUse,
    );

    const convertedHeadings = FormJsonToMarkdownConverter.mapHeadings(form, headingsToUse);

    return {
      id: form.id.toString(),
      folderId: form.folderId,
      title: form.title,
      description: form.description ?? '',
      tags: (form.tags ?? []).map((t) => t.key),
      createdBy: form.createdBy ?? 'undefined',
      category: form.category?.key ?? '',
      coverImage: form.coverImage,
      thumbnail: form.thumbnail,
      sections: convertedSections,
      headings: convertedHeadings,
    };
  }

  private static mapSections(
    form: Form,
    questions: Question<QuestionProps>[],
    sectionsOverride?: Section[],
  ): QuizSection[] {
    const sections = sectionsOverride ?? form.sections ?? [];
    return sections.map((section, sectionIndex) =>
      FormJsonToMarkdownConverter.mapSection(section, questions, form.id.toString(), sectionIndex),
    );
  }

  private static mapSection(
    section: Section,
    allQuestions: Question<QuestionProps>[],
    formId: string,
    sectionIndex: number,
  ): QuizSection {
    const sectionQuestions = allQuestions.filter((q) => q.sectionId === section.id.toString());
    const quizQuestions = sectionQuestions.map((q, idx) =>
      FormJsonToMarkdownConverter.mapQuestionToQuiz(q, sectionQuestions, idx),
    );

    return {
      id: section.id.toString(),
      title: section.title,
      description: section.description ?? '',
      index: sectionIndex + 1,
      previousSectionId: section.previousSectionId === 'none' ? null : section.previousSectionId,
      questions: quizQuestions,
    };
  }

  private static mapQuestionToQuiz(
    question: Question<QuestionProps>,
    sortedQuestions: Question<QuestionProps>[],
    index: number,
  ): QuizQuestion {
    const displayIndex = (question.index ?? index) + 1;
    const idx = sortedQuestions.indexOf(question);
    const nextQuestion = sortedQuestions[idx + 1];
    const prevQuestion = sortedQuestions[idx - 1];
    const prevId = question.previousQuestionId;
    const nextId = nextQuestion?.id.toString();

    const base = {
      id: question.id.toString(),
      description: question.description ?? '',
      type: question.type,
      index: displayIndex,
      score: question.score ?? 0,
      explanation: question.explanation,
      previousQuestionId: prevId === NoneValue || !prevId ? null : prevId,
      nextQuestionId: nextId ?? null,
    };

    switch (question.type) {
      case QuestionType.Essay: {
        return { ...base };
      }
      case QuestionType.SingleChoice:
      case QuestionType.MultipleChoice: {
        const answers = FormJsonToMarkdownConverter.mapAnswers(question);
        const correctAnswers = FormJsonToMarkdownConverter.mapCorrectAnswers(question, false);
        return {
          ...base,
          answers,
          correctAnswers,
        };
      }
      case QuestionType.FillInBlank: {
        const answers = FormJsonToMarkdownConverter.mapAnswers(question);
        const correctAnswers = FormJsonToMarkdownConverter.mapCorrectAnswers(question, false);
        return {
          ...base,
          answers,
          correctAnswers,
        };
      }
      case QuestionType.Matching: {
        const answers = FormJsonToMarkdownConverter.mapAnswers(question);
        const correctAnswers = FormJsonToMarkdownConverter.mapCorrectAnswers(question, true);
        const subQuestions = FormJsonToMarkdownConverter.mapSubQuestions(question);
        return {
          ...base,
          answers,
          correctAnswers,
          subQuestions,
        };
      }
      default:
        return { ...base };
    }
  }

  private static mapAnswers(question: Question<QuestionProps>): QuizAnswer[] {
    const answers = question.answers ?? [];
    return answers.map((a, idx) => ({
      id: a.id.toString(),
      description: a.description ?? '',
      index: idx,
    }));
  }

  private static mapCorrectAnswers(
    question: Question<QuestionProps>,
    includeSubQuestionId: boolean,
  ): QuizCorrectAnswer[] {
    // Handle possible missing property: 'correctAnswers'
    const correctAnswers = (question as any).correctAnswers ?? [];
    return correctAnswers.map((c: any) => {
      const result: QuizCorrectAnswer = {
        id: c.id?.toString?.() ?? '',
        answerId: c.answerId?.toString?.() ?? '',
        score: (c as { score?: { value: number } }).score?.value ?? 1,
      };
      if (includeSubQuestionId && 'subQuestionId' in c) {
        result.subQuestionId = (c as { subQuestionId: string }).subQuestionId;
      }
      return result;
    });
  }

  private static mapSubQuestions(question: Question<QuestionProps>): QuizSubQuestion[] {
    const subQuestions = question.subQuestions ?? [];
    return subQuestions.map((s) => ({
      id: s.id.toString(),
      description: s.description ?? '',
      index: s.index,
    }));
  }

  private static mapHeadings(form: Form, headingsOverride?: Heading[]): QuizHeading[] {
    const headings = headingsOverride ?? form.headings ?? [];
    return headings.map((h: Heading, index: number) => ({
      id: h.id.toString(),
      title: h.title,
      description: h.description,
      formId: form.id.toString(),
      sectionId: h.sectionId || '',
      index: index + 1,
      previousQuestionId: h.previousQuestionId === NoneValue ? undefined : h.previousQuestionId,
      nextQuestionId: h.nextQuestionId === NoneValue ? undefined : h.nextQuestionId,
      previousHeadingId: h.previousHeadingId === NoneValue ? null : h.previousHeadingId,
      nextHeadingId: h.nextHeadingId === NoneValue ? null : h.nextHeadingId,
    }));
  }

  private static quizDataToMarkdown(quizData: QuizData, primaryIds?: Set<string>): string {
    const formBlock = FormJsonToMarkdownConverter.convertForm(quizData);
    const sectionsBlock = FormJsonToMarkdownConverter.convertSections(
      quizData.sections,
      quizData.headings,
      primaryIds,
    );
    return [formBlock, sectionsBlock].join('\n');
  }

  private static processHtmlContent(content: string): string {
    if (!content) return content;

    let processed = content;

    processed = processed.replace(
      /<(?:strong|b)>\s*(<fib-blank\b[^>]*id=(?:"[^"]*"|'[^']*')[^>]*\/?>\s*(?:<\/fib-blank>)?)\s*<\/(?:strong|b)>/gi,
      '**__**',
    );
    processed = processed.replace(
      /<fib-blank\b[^>]*id=(?:"[^"]*"|'[^']*')[^>]*\/?>\s*(?:<\/fib-blank>)?/gi,
      '**__**',
    );
    processed = processed.replace(/<\/fib-blank>/gi, '');
    processed = processed.replace(/<strong>__<\/strong>/g, '**\\_\\_**');
    processed = processed.replace(/<strong>_<\/strong>/g, '**\\_**');
    processed = processed.replace(/<\/br>/g, '\n');
    processed = processed.replace(/<br\s*\/?>/g, '\n');
    processed = processed.replace(/\n\n+/g, '\n\n');

    processed = processed.replace(/<ul>([\s\S]*?)<\/ul>/g, (match, listContent) => {
      const items = listContent.match(/<li>([\s\S]*?)<\/li>/g);
      if (!items) return match;
      const markdownItems = items.map((item: string) => {
        const itemText = item.replace(/<li>([\s\S]*?)<\/li>/, '$1').trim();
        return `- ${itemText}`;
      });
      return '\n\n' + markdownItems.join('\n') + '\n\n';
    });

    processed = processed.replace(/<ol>([\s\S]*?)<\/ol>/g, (match, listContent) => {
      const items = listContent.match(/<li>([\s\S]*?)<\/li>/g);
      if (!items) return match;
      const markdownItems = items.map((item: string, index: number) => {
        const itemText = item.replace(/<li>([\s\S]*?)<\/li>/, '$1').trim();
        return `${index + 1}. ${itemText}`;
      });
      return '\n\n' + markdownItems.join('\n') + '\n\n';
    });

    processed = processed.replace(
      /<img\s+src="([^"]*)"(?:\s+alt="([^"]*)")?[^>]*\/?>/g,
      (_match, src, alt) => {
        const altText = alt || 'image';
        return `![${altText}](${src})`;
      },
    );

    processed = processed.replace(/<strong>([\s\S]*?)<\/strong>/g, '**$1**');
    processed = processed.replace(/<b>([\s\S]*?)<\/b>/g, '**$1**');
    processed = processed.replace(/<em>([\s\S]*?)<\/em>/g, '*$1*');
    processed = processed.replace(/<i>([\s\S]*?)<\/i>/g, '*$1*');
    processed = processed.replace(/\n{3,}/g, '\n\n');
    processed = processed.trim();

    return processed;
  }

  private static convertForm(data: QuizData): string {
    const lines: string[] = [];
    lines.push(`# [Form: ${data.title}]`);
    lines.push('');
    lines.push(`@Id: ${data.id}`);
    lines.push('');
    lines.push(`@FolderId: ${data.folderId}`);
    lines.push('');
    lines.push(`@Category: ${data.category}`);
    lines.push('');
    lines.push(`@Tags: ${data.tags.join(', ')}`);
    lines.push('');

    if (data.coverImage) {
      lines.push(`@CoverImage: ![image](${data.coverImage})`);
      lines.push('');
    }
    if (data.thumbnail) {
      lines.push(`@Thumbnail: ![image](${data.thumbnail})`);
      lines.push('');
    }
    if (data.description) {
      lines.push(`@Content: ${FormJsonToMarkdownConverter.processHtmlContent(data.description)}`);
      lines.push('');
    }
    lines.push(`@CreateBy: ${data.createdBy || 'undefined'}`);
    return lines.join('\n');
  }

  private static convertSections(
    sections: QuizSection[],
    headings: QuizHeading[],
    primaryIds?: Set<string>,
  ): string {
    const result: string[] = [];
    for (const section of sections) {
      result.push('');
      result.push(`# [Section: ${section.title}]`);
      result.push('');
      result.push(`@Id: ${section.id}`);
      result.push('');
      if (primaryIds?.has(section.id)) {
        result.push('@Primary');
        result.push('');
      }
      if (section.index != null) {
        result.push(`@Index: ${section.index}`);
        result.push('');
      }
      if (section.previousSectionId) {
        result.push(`@PreviousSectionId: ${section.previousSectionId}`);
        result.push('');
      }
      result.push(
        `@Content: ${FormJsonToMarkdownConverter.processHtmlContent(section.description)}`,
      );
      const sectionHeadings = headings.filter((h) => h.sectionId === section.id);
      const orderedQuestions = FormJsonToMarkdownConverter.orderQuestionsByChain(section.questions);
      result.push(
        FormJsonToMarkdownConverter.processHeadingsAndQuestions(
          sectionHeadings,
          orderedQuestions,
          primaryIds,
        ),
      );
    }
    return result.join('\n');
  }

  private static orderQuestionsByChain(questions: QuizQuestion[]): QuizQuestion[] {
    if (questions.length === 0) return [];

    const questionMap = new Map<string, QuizQuestion>();
    questions.forEach((q) => questionMap.set(q.id, q));

    let firstQuestion = questions.find(
      (q) => !q.previousQuestionId || q.previousQuestionId === 'none',
    );
    if (!firstQuestion) {
      const referencedIds = new Set(questions.map((q) => q.nextQuestionId).filter(Boolean));
      firstQuestion = questions.find((q) => !referencedIds.has(q.id)) ?? questions[0];
    }

    const ordered: QuizQuestion[] = [];
    let current: QuizQuestion | undefined = firstQuestion;
    const visited = new Set<string>();

    while (current && !visited.has(current.id)) {
      visited.add(current.id);
      ordered.push(current);
      const nextId = current.nextQuestionId;
      current = nextId && questionMap.has(nextId) ? questionMap.get(nextId) : undefined;
    }

    questions.forEach((q) => {
      if (!visited.has(q.id)) ordered.push(q);
    });
    return ordered;
  }

  private static processHeadingsAndQuestions(
    headings: QuizHeading[],
    questions: QuizQuestion[],
    primaryIds?: Set<string>,
  ): string {
    const result: string[] = [];
    const processedQuestions = new Set<string>();

    for (const heading of headings) {
      result.push('');
      result.push(`## [Heading: ${heading.title}]`);
      result.push('');
      result.push(`@Id: ${heading.id}`);
      result.push('');
      if (primaryIds?.has(heading.id)) {
        result.push('@Primary');
        result.push('');
      }
      if (heading.index != null) {
        result.push(`@Index: ${heading.index}`);
        result.push('');
      }
      if (heading.previousQuestionId) {
        result.push(`@PreviousQuestionId: ${heading.previousQuestionId}`);
        result.push('');
      }
      if (heading.nextQuestionId) {
        result.push(`@NextQuestionId: ${heading.nextQuestionId}`);
        result.push('');
      }
      if (heading.previousHeadingId) {
        result.push(`@PreviousHeadingId: ${heading.previousHeadingId}`);
        result.push('');
      }
      if (heading.nextHeadingId) {
        result.push(`@NextHeadingId: ${heading.nextHeadingId}`);
        result.push('');
      }
      if (heading.description) {
        result.push('@Content:');
        result.push(FormJsonToMarkdownConverter.processHtmlContent(heading.description));
      } else {
        result.push('@Content:');
      }

      if (heading.nextQuestionId) {
        const question = questions.find((q) => q.id === heading.nextQuestionId);
        if (question && !processedQuestions.has(question.id)) {
          result.push('');
          result.push(FormJsonToMarkdownConverter.convertQuestion(question, primaryIds));
          processedQuestions.add(question.id);
        }
      }
    }

    questions.forEach((question) => {
      if (!processedQuestions.has(question.id)) {
        result.push('');
        result.push(FormJsonToMarkdownConverter.convertQuestion(question, primaryIds));
        processedQuestions.add(question.id);
      }
    });

    return result.join('\n');
  }

  private static convertQuestion(question: QuizQuestion, primaryIds?: Set<string>): string {
    const lines: string[] = [];
    const questionType = FormJsonToMarkdownConverter.formatQuestionType(question.type);
    lines.push(`## [QuestionType: ${questionType}, Score: ${question.score}]`);
    lines.push('');
    lines.push(`@Id: ${question.id}`);
    lines.push('');
    if (primaryIds?.has(question.id)) {
      lines.push('@Primary');
      lines.push('');
    }
    if (question.index != null) {
      lines.push(`@Index: ${question.index}`);
      lines.push('');
    }
    if (question.previousQuestionId) {
      lines.push(`@PreviousQuestionId: ${question.previousQuestionId}`);
      lines.push('');
    }
    if (question.nextQuestionId) {
      lines.push(`@NextQuestionId: ${question.nextQuestionId}`);
      lines.push('');
    }
    lines.push(`@Content: ${FormJsonToMarkdownConverter.processHtmlContent(question.description)}`);

    switch (question.type) {
      case QuestionType.Essay:
        break;
      case QuestionType.SingleChoice:
      case QuestionType.MultipleChoice:
        lines.push('');
        lines.push(FormJsonToMarkdownConverter.convertChoiceAnswers(question));
        break;
      case QuestionType.FillInBlank:
        lines.push('');
        lines.push(FormJsonToMarkdownConverter.convertFillInBlankAnswers(question));
        break;
      case QuestionType.Matching:
        lines.push('');
        lines.push(FormJsonToMarkdownConverter.convertMatchingAnswers(question));
        break;
    }

    if (question.explanation) {
      lines.push('');
      lines.push('### Explanation');
      lines.push('');
      lines.push(question.explanation);
    }

    return lines.join('\n');
  }

  private static formatQuestionType(type: QuestionType): string {
    switch (type) {
      case QuestionType.SingleChoice:
        return 'SingleChoice';
      case QuestionType.MultipleChoice:
        return 'MultipleChoice';
      case QuestionType.FillInBlank:
        return 'Fill-in-blank';
      case QuestionType.Matching:
        return 'Matching';
      case QuestionType.Essay:
        return 'Essay';
    }
  }

  private static convertChoiceAnswers(
    question: QuizQuestion & { answers?: QuizAnswer[]; correctAnswers?: QuizCorrectAnswer[] },
  ): string {
    const lines: string[] = [];
    lines.push('### Answers');
    lines.push('');
    if (question.answers) {
      question.answers.forEach((answer, index) => {
        lines.push(`${index + 1} ${answer.description}`);
        lines.push('');
        lines.push(`@Id: ${answer.id}`);
        lines.push('');
      });
    }
    lines.push('### CorrectAnswers');
    lines.push('');
    if (question.correctAnswers) {
      question.correctAnswers.forEach((_, index) => {
        const answerIndex = question.answers?.findIndex(
          (a) => a.id === question.correctAnswers![index].answerId,
        );
        if (answerIndex !== undefined && answerIndex >= 0) {
          lines.push(`${index + 1} [CorrectAnswer: ${answerIndex + 1}, Score: 1]`);
          lines.push('');
        }
      });
    }
    return lines.join('\n').trim();
  }

  private static convertFillInBlankAnswers(
    question: QuizQuestion & { answers?: QuizAnswer[]; correctAnswers?: QuizCorrectAnswer[] },
  ): string {
    const lines: string[] = [];
    lines.push('### CorrectAnswers');
    lines.push('');
    if (question.correctAnswers) {
      question.correctAnswers.forEach((correctAnswer, index) => {
        const answer = question.answers?.find((a) => a.id === correctAnswer.answerId);
        const answerText = answer?.description ?? 'ABC';
        lines.push(`${index + 1} [CorrectAnswer: ${answerText}, Score: 1]`);
        lines.push('');
      });
    }
    return lines.join('\n').trim();
  }

  private static convertMatchingAnswers(
    question: QuizQuestion & {
      answers?: QuizAnswer[];
      correctAnswers?: QuizCorrectAnswer[];
      subQuestions?: QuizSubQuestion[];
    },
  ): string {
    const lines: string[] = [];
    lines.push('### Answers');
    lines.push('');
    if (question.answers) {
      question.answers.forEach((answer) => {
        lines.push(`${answer.description}`);
        lines.push('');
        lines.push(`@Id: ${answer.id}`);
        lines.push('');
      });
    }
    lines.push('### SubQuestions');
    lines.push('');
    if (question.subQuestions) {
      question.subQuestions.forEach((subQuestion, index) => {
        lines.push(`${index + 1} ${subQuestion.description}`);
        lines.push('');
        lines.push(`@Id: ${subQuestion.id}`);
        lines.push('');
      });
    }
    lines.push('### CorrectAnswers');
    lines.push('');
    if (question.correctAnswers) {
      question.correctAnswers.forEach((correctAnswer, index) => {
        const answerIndex = question.answers?.findIndex((a) => a.id === correctAnswer.answerId);
        if (answerIndex !== undefined && answerIndex >= 0) {
          lines.push(`${index + 1} [CorrectAnswer: ${answerIndex + 1}, Score: 1]`);
          lines.push('');
        }
      });
    }
    return lines.join('\n').trim();
  }
}
